import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import usePromiseVotes from "hooks/promises/usePromiseVotes";
import useAuth from "hooks/auth/useAuth";
import { calculatePoints } from "libs/utils";
import LoginDialog from "components/card/LoginDialog";
import VerifyEmailDialog from "components/card/VerifyEmailDialog";
import EndDate from "components/date/EndDate";
import useLikes from "hooks/predictions/useLikes";
import CommentSection from "components/social/CommentSection";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs } from "firebase/firestore";
import { db } from "config/firebase";

const PromiseShortsCard = ({ promise, onVote }) => {
  const router = useRouter();
  const { handleVote } = usePromiseVotes();
  const { currentUser } = useAuth();
  const [currentCard, setCurrentCard] = useState(promise);

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenVerifyEmailDialog, setIsOpenVerifyEmailDialog] = useState(false);

  useEffect(() => {
    setCurrentCard(promise);
  }, [promise]);

  const { likeCount, toggleLike, checkIfLiked } = useLikes(
    `promises/${promise.id}`,
  );
  const isLiked = checkIfLiked(currentUser?.uid);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    if (!promise.id) return;
    const commentsRef = collection(db, "promises", promise.id, "comments");
    // Use a one-time fetch for shorts view to reduce listeners and optimize swipe performance
    getDocs(commentsRef).then((snapshot) => {
      setCommentCount(snapshot.size);
    });
  }, [promise.id]);

  if (!currentCard) return null;

  const { agreeCount, disagreeCount } = currentCard.votes
    ? calculatePoints(currentCard.votes)
    : { agreeCount: 0, disagreeCount: 0 };

  const givenTime = new Date(currentCard?.deadline);
  const now = new Date();
  const isPast = givenTime < now;
  const isFinished = currentCard.type === "finished";
  const isDisabled = isPast || isFinished;

  const userVote = currentCard.votes?.[currentUser?.uid] || "";

  const toggleVote = (voteType) => {
    if (!currentUser) {
      setIsOpen(true);
      return;
    }
    if (currentUser && !currentUser.emailVerified) {
      if (currentUser.providerData?.[0].providerId !== "facebook.com") {
        setIsOpenVerifyEmailDialog(true);
        return;
      }
    }
    if (!isDisabled) {
      const newVote = userVote === voteType ? "" : voteType;
      handleVote(currentCard.id, currentUser?.uid, newVote);
      setCurrentCard((prev) => ({
        ...prev,
        votes: {
          ...prev.votes,
          [currentUser?.uid]: newVote,
        },
      }));
      if (onVote && newVote !== "") {
        onVote();
      }
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/promises/${currentCard.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: currentCard.title,
          text: currentCard.description,
          url: url,
        });
      } catch (err) {
        console.log("Share failed:", err);
      }
    } else {
      navigator.clipboard.writeText(url);
      alert("Հղումը պատճենվեց:"); // Link copied
    }
  };

  return (
    <div className="relative w-full h-[100%] max-h-[100%] aspect-[9/16] bg-[#030014] md:rounded-[24px] rounded-none overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.8)] border-0 md:border border-cyan-500/20 group shorts-container">
      {/* Background Media */}
      <div className="absolute inset-0 z-0">
        <Image
          src={
            currentCard.image ||
            currentCard.predictor?.image ||
            "/default_video.webp"
          }
          alt={currentCard.title}
          fill
          className="object-cover transition-transform duration-1000 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, 450px"
          quality={75}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent h-1/3" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent h-2/3" />
      </div>

      {/* Floating Action Buttons (Right Side) */}
      <div
        className="absolute right-2 md:right-4 bottom-[25%] md:bottom-[20%] flex flex-col items-center gap-[24px] z-30"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Agree Button */}
        <div
          className={`flex flex-col items-center gap-1 group/btn ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          onClick={() => toggleVote("agree")}
        >
          <div
            className={`p-3 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${userVote === "agree" ? "bg-cyan-500 text-white shadow-[0_0_20px_rgba(102,133,255,0.5)]" : "bg-black/40 text-white hover:bg-black/60"}`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="28"
              height="28"
              className="drop-shadow-lg text-white"
            >
              <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
            </svg>
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {agreeCount}
          </span>
        </div>

        {/* Disagree Button */}
        <div
          className={`flex flex-col items-center gap-1 group/btn ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          onClick={() => toggleVote("disagree")}
        >
          <div
            className={`p-3 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${isDisabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"} ${userVote === "disagree" ? "bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.5)]" : "bg-black/40 text-white hover:bg-black/60"}`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="28"
              height="28"
              className="drop-shadow-lg text-white"
            >
              <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
            </svg>
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {disagreeCount}
          </span>
        </div>

        {/* Share Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={handleShare}
        >
          <div className="p-3 rounded-full flex items-center justify-center bg-black/40 text-white backdrop-blur-md hover:bg-black/60 transition-all duration-300">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="28"
              height="28"
              className="drop-shadow-lg text-white"
            >
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
            </svg>
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            Կիսվել
          </span>
        </div>

        {/* Promise Like Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={() => toggleLike(currentUser?.uid)}
        >
          <div
            className={`p-3 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${isLiked ? "bg-rose-500 text-white shadow-[0_0_20px_rgba(244,63,94,0.5)]" : "bg-black/40 text-white hover:bg-black/60"}`}
          >
            {isLiked ? (
              <FavoriteIcon sx={{ fontSize: 28 }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: 28 }} />
            )}
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {likeCount || 0}
          </span>
        </div>

        {/* Comment Toggle Button */}
        <div
          className="flex flex-col items-center gap-1 group/btn cursor-pointer"
          onClick={() => setShowComments(true)}
        >
          <div className="p-3 rounded-full flex items-center justify-center bg-black/40 text-white backdrop-blur-md hover:bg-black/60 transition-all duration-300">
            <ChatBubbleOutlineIcon sx={{ fontSize: 28 }} />
          </div>
          <span className="text-white text-[12px] font-bold drop-shadow-md">
            {commentCount || 0}
          </span>
        </div>
      </div>

      {/* Comment Overlay for Shorts */}
      {showComments && (
        <div className="absolute inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowComments(false)}
          />
          <div className="relative h-full bg-[#030014]/95 light:bg-white border-t border-cyan-500/30 light:border-slate-200 flex flex-col animate-in slide-in-from-bottom duration-300 overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-cyan-500/10 light:border-slate-100">
              <h3 className="text-white light:text-slate-900 font-bold tracking-wider">
                Մեկնաբանություններ
              </h3>
              <button
                onClick={() => setShowComments(false)}
                className="p-1 rounded-full hover:bg-white/10 light:hover:bg-black/5 text-white/70 light:text-slate-500"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <CommentSection
                docPath={`promises/${promise.id}`}
                isInModal={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Metadata (Bottom Left) */}
      <div
        className="absolute bottom-[100px] md:bottom-6 left-4 right-20 flex flex-col items-start gap-[12px] z-20 pointer-events-none"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Status / Level Badges */}
        <div className="flex items-center gap-2">
          <span className="bg-cyan-500/80 backdrop-blur-sm border border-cyan-400 text-white rounded-lg px-2 py-1 text-[10px] md:text-[12px]  tracking-widest uppercase shadow-[0_0_10px_rgba(102,133,255,0.2)]">
            {currentCard.level} միավոր
          </span>
          {isDisabled && (
            <span className="bg-red-500/80 backdrop-blur-sm border border-red-500 text-white rounded-lg px-2 py-1 text-[10px] md:text-[12px]  tracking-widest uppercase shadow-[0_0_10px_rgba(239,68,68,0.2)]">
              Ավարտված
            </span>
          )}
        </div>

        {/* Title */}
        <h2
          onClick={() => router.push(`/promises/${currentCard.id}`)}
          className="text-white text-[18px] md:text-[22px] font-bold leading-tight drop-shadow-lg cursor-pointer pointer-events-auto hover:text-cyan-300 transition-colors line-clamp-3"
        >
          {currentCard.title}
        </h2>

        {/* Description */}
        {currentCard.description && (
          <p className="text-white/75 text-[12px] md:text-[13px] leading-relaxed drop-shadow-md bg-black/20 backdrop-blur-sm rounded-lg px-2 py-1.5 border border-white/5">
            {currentCard.description}
          </p>
        )}

        {currentCard.predictor && (
          <div
            className="flex items-center gap-3 mt-2 pointer-events-auto cursor-pointer p-2 rounded-xl bg-black/30 backdrop-blur-md hover:bg-black/50 transition-all border border-white/10"
            onClick={() =>
              router.push(`/predictors/${currentCard.predictor.id}`)
            }
          >
            <div className="w-10 h-10 rounded-full overflow-hidden border border-cyan-400/50 shadow-[0_0_10px_rgba(102,133,255,0.3)] shrink-0">
              <Image
                src={currentCard.predictor.image || "/defaultuserprofile.webp"}
                alt="Predictor"
                width={40}
                height={40}
                className="object-cover w-full h-full"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-white text-[14px] font-bold flex items-center gap-1 drop-shadow-md leading-tight">
                {currentCard.predictor.firstName}{" "}
                {currentCard.predictor.lastName}
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="14"
                  height="14"
                  className="text-cyan-400"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </span>
              <span className="text-white/70 text-[11px]  tracking-widest">
                ԿԱՆԽԱՏԵՍՈՂ
              </span>
            </div>
          </div>
        )}

        {/* Deadline */}
        {currentCard.deadline && (
          <div className="mt-2 pointer-events-auto">
            <EndDate deadline={currentCard.deadline} />
          </div>
        )}
      </div>

      <LoginDialog isOpen={isOpen} handleClose={() => setIsOpen(false)} />
      <VerifyEmailDialog
        isOpen={isOpenVerifyEmailDialog}
        handleClose={() => setIsOpenVerifyEmailDialog(false)}
      />
    </div>
  );
};

export default PromiseShortsCard;
